// SPA-ish navigation between markdown documents served by the CLI.
//
// Every internal link or image carries an absolute POSIX path resolved against
// the *current* markdown document's directory; the CLI's whitelist accepts
// such paths only if the loaded markdown actually references them.

import type { Transport } from "./transport";
import { buildRenderer } from "./renderer/markdown";
import { renderMathIn } from "./renderer/math";
import { renderMermaidIn } from "./renderer/mermaid";
import { highlightIn } from "./renderer/highlight";

const md = buildRenderer();
const blobUrls = new Set<string>();

export interface NavOptions {
  transport: Transport;
  root: HTMLElement;
}

export interface NavController {
  /** Re-render the current document if `changedPath` is part of it. */
  reloadIfAffected(changedPath: string): void;
}

let current: { dir: string; path: string; deps: Set<string> } | null = null;

export async function loadInitial(
  opts: NavOptions,
  initialPath: string,
): Promise<NavController> {
  await navigate(opts, initialPath);
  installLinkInterceptor(opts);
  return {
    reloadIfAffected(changedPath: string) {
      if (!current) return;
      if (changedPath !== current.path && !current.deps.has(changedPath)) return;
      void navigate(opts, current.path);
    },
  };
}

async function navigate(opts: NavOptions, absPath: string): Promise<void> {
  // Revoke previously created blob URLs.
  for (const u of blobUrls) URL.revokeObjectURL(u);
  blobUrls.clear();

  const res = await opts.transport.get(absPath);
  if (res.status !== 200) {
    opts.root.innerHTML = `<p class="error">Failed to load <code>${escapeHtml(absPath)}</code> (status ${res.status}).</p>`;
    return;
  }
  const text = new TextDecoder("utf-8").decode(res.bytes);
  const dir = dirname(absPath);
  const deps = new Set<string>();
  current = { dir, path: absPath, deps };

  const html = md.render(text);
  opts.root.innerHTML = html;

  rewriteRelativeLinks(opts.root, dir);
  await Promise.all([
    resolveImages(opts.root, opts.transport, deps),
    renderMermaidIn(opts.root),
  ]);
  // Math + syntax-highlight can run after mermaid removes its <pre> blocks.
  await Promise.all([
    renderMathIn(opts.root),
    highlightIn(opts.root),
  ]);

  window.scrollTo({ top: 0 });
  document.title = `${basename(absPath)} — mdview`;
}

function installLinkInterceptor(opts: NavOptions): void {
  opts.root.addEventListener("click", (e) => {
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>("a[data-mdview-internal]");
    if (!a) return;
    const target = a.dataset.mdviewTarget;
    if (!target) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    void navigate(opts, target);
  });
}

function rewriteRelativeLinks(root: HTMLElement, dir: string): void {
  for (const a of root.querySelectorAll<HTMLAnchorElement>("a[href]")) {
    const href = a.getAttribute("href") ?? "";
    if (!isInternal(href)) continue;
    const [pathPart, frag] = splitFragment(href);
    if (pathPart === "" && frag) continue; // pure fragment, leave alone
    const abs = resolvePosix(dir, pathPart);
    a.dataset.mdviewInternal = "1";
    a.dataset.mdviewTarget = abs;
    a.setAttribute("href", `#${abs}${frag ? `#${frag}` : ""}`);
  }
}

async function resolveImages(
  root: HTMLElement,
  transport: Transport,
  deps: Set<string>,
): Promise<void> {
  const imgs = Array.from(root.querySelectorAll<HTMLImageElement>("img[src]"));
  await Promise.all(imgs.map(async (img) => {
    const src = img.getAttribute("src") ?? "";
    if (!isInternal(src)) return;
    if (!current) return;
    const abs = resolvePosix(current.dir, src);
    deps.add(abs);
    try {
      const res = await transport.get(abs);
      if (res.status !== 200) { img.alt = `[404] ${abs}`; return; }
      const blob = new Blob([res.bytes as BlobPart], { type: res.mime });
      const url = URL.createObjectURL(blob);
      blobUrls.add(url);
      img.src = url;
    } catch (err) {
      img.alt = `[error] ${(err as Error).message}`;
    }
  }));
}

// ---- path helpers (POSIX) ----

function isInternal(href: string): boolean {
  if (!href) return false;
  if (href.startsWith("#")) return false;
  if (/^[a-z][a-z0-9+.-]*:/i.test(href)) return false;  // any scheme
  if (href.startsWith("//")) return false;
  return true;
}

function splitFragment(href: string): [string, string] {
  const i = href.indexOf("#");
  if (i < 0) return [href, ""];
  return [href.slice(0, i), href.slice(i + 1)];
}

function resolvePosix(base: string, rel: string): string {
  if (rel.startsWith("/")) return normalize(rel);
  return normalize(`${base}/${rel}`);
}

function normalize(p: string): string {
  const parts: string[] = [];
  for (const seg of p.split("/")) {
    if (seg === "" || seg === ".") continue;
    if (seg === "..") parts.pop();
    else parts.push(seg);
  }
  return "/" + parts.join("/");
}

function dirname(p: string): string {
  const i = p.lastIndexOf("/");
  if (i <= 0) return "/";
  return p.slice(0, i);
}

function basename(p: string): string {
  return p.slice(p.lastIndexOf("/") + 1);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
